import {
  CdkDragDrop,
  DragDropModule,
  moveItemInArray,
} from '@angular/cdk/drag-drop';
import { CommonModule, NgClass } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  OnInit,
  Output,
  inject,
  signal,
  ChangeDetectionStrategy,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  DialogService,
  DynamicDialogRef,
} from '../../../../core/services/dialog';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { AlertService } from '../../../../core/services/alert/alert.service';
import { DashboardService } from '../../../../core/services/dashboard/dashboard.service';
import { LayoutService } from '../../../../core/services/layout/layout.service';
import { WebSockerSerive } from '../../../../core/services/socket/socket.service';
import { UserGuidanceService } from '../../../../shared/services/analytics/user-guidance.service';
import { TranslatePipe } from '../../../pipes/translate/translate.pipe';
import { FolderCardComponent } from '../../cards/folder-card/folder-card.component';
import { IcGlobeComponent } from '../../Icons/ic-globe/ic-globe';
import { IcParcelBoxComponent } from '../../Icons/ic-parcelbox/ic-parcelbox';
import { IcPlaneComponent } from '../../Icons/ic-plane/ic-plane';
import { IcTasksComponent } from '../../Icons/ic-tasks/ic-tasks.component';

/**
 * Interface representing a clickable card in the dashboard footer.
 */
interface DashboardCard {
  /** Unique identifier for the card and its SVG elements. */
  id: string;
  /** Display title for the card. */
  title: string;
  /** Icon name mapping to specific component logic. */
  icon: string;
  /** Routing path for navigation. */
  route: string;
  /** Optional notification count. */
  badgeCount?: number;
  /** Label for the notification badge. */
  badgeLabel?: string;
  /** Whether the card should display its 'Active' state styles. */
  isHighlighted?: boolean;
}

/**
 * @component DashboardFooterComponent
 * @description
 * A sticky footer component for the main dashboard. Features a grid of interactive cards
 * with dynamic SVG backgrounds, notification badges, and smooth hover animations.
 * Synchronizes its state with the active Angular route and communicates its height
 * to the LayoutService for content spacing.
 */
@Component({
  selector: 'app-dashboard-footer',
  standalone: true,
  imports: [
    CommonModule,
    NgClass,
    IcPlaneComponent,
    IcParcelBoxComponent,
    IcTasksComponent,
    IcGlobeComponent,
    FolderCardComponent,
    DragDropModule,
  ],
  templateUrl: './dashboard-footer.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard-footer.component.scss',
})
export class DashboardFooterComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  /** Emitted when a dashboard card is clicked. */
  @Output() cardClick = new EventEmitter<DashboardCard>();

  /** Controls the visibility of any drawer. */
  drawerVisible = false;

  /** Injected service for dialog service. */
  private readonly dialogService = inject(DialogService);
  /** Property dialog ref. */
  private dialogRef: DynamicDialogRef | undefined | null;

  /** Injected service for element ref. */
  private readonly elementRef = inject(ElementRef);
  /** Injected service for layout service. */
  private readonly layoutService = inject(LayoutService);
  /** Injected service for router. */
  private readonly router = inject(Router);
  /** Injected service for dashboard service. */
  private readonly dashboardService = inject(DashboardService);
  /** Injected service for alert service. */
  private readonly alertService = inject(AlertService);
  /** Injected service for socket service. */
  private readonly socketService = inject(WebSockerSerive);
  /** Injected service for guidance service. */
  public readonly guidanceService = inject(UserGuidanceService);
  /** Injected service for translate. */
  public readonly translate = inject(TranslatePipe);

  /** Observer to detect changes in footer height and notify the layout system. */
  private resizeObserver: ResizeObserver | null = null;
  /** Property alert socket sub. */
  private alertSocketSub?: Subscription;

  /** List of dashboard navigation cards. */
  cards = signal<DashboardCard[]>([
    {
      id: 'declarations',
      title: this.translate.transform('declarations'),
      icon: 'box',
      route: '/dashboard/declarations',
    },
    {
      id: 'shipments',
      title: this.translate.transform('shipments'),
      icon: 'airplane',
      route: '/dashboard/shipments',
    },
    {
      id: 'tasks',
      title: this.translate.transform('tasks'),
      icon: 'tasks',
      route: '/dashboard/tickets',
    },
    {
      id: 'settings',
      title: this.translate.transform('globalModules'),
      icon: 'globe',
      route: '/dashboard/settings',
    },
  ]);

  /**
   * Handles load footer config logic.
   */
  private loadFooterConfig() {
    const config =
      this.dashboardService.getUserPreferences()?.tableSettings
        ?.dashboardFooterConfig;
    if (config && Array.isArray(config)) {
      const currentCards = this.cards();
      const orderedCards = config
        .map((id) => currentCards.find((c) => c.id === id))
        .filter((c): c is DashboardCard => c !== undefined);

      const missingCards = currentCards.filter((c) => !config.includes(c.id));
      this.cards.set([...orderedCards, ...missingCards]);
    }
  }

  /**
   * Handles save footer config logic.
   */
  private saveFooterConfig() {
    const config = this.cards().map((c) => c.id);
    this.dashboardService.saveFooterConfig(config);
  }

  /**
   * Handles on card drop logic.
   * @param event - The event
   */
  onCardDrop(event: CdkDragDrop<DashboardCard[]>) {
    const currentCards = [...this.cards()];
    moveItemInArray(currentCards, event.previousIndex, event.currentIndex);
    this.cards.set(currentCards);
    this.saveFooterConfig();
  }

  /**
   * Lifecycle hook that runs on component initialization.
   */
  ngOnInit() {
    this.loadFooterConfig();
    // Monitor router events to automatically highlight the correct card
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event) => {
        const navEnd = event as NavigationEnd;
        this.updateHighlightedCard(navEnd.url);
      });

    this.updateHighlightedCard(this.router.url);
  }

  /**
   * Lifecycle hook that runs after component view initialization.
   */
  ngAfterViewInit() {
    this.updateFooterHeight();

    // Re-calculate height on window/element resize to avoid content overlap
    this.resizeObserver = new ResizeObserver(() => {
      this.updateFooterHeight();
    });
    this.resizeObserver.observe(this.elementRef.nativeElement);
  }

  /**
   * Lifecycle hook that cleans up subscriptions on component destruction.
   */
  ngOnDestroy() {
    this.resizeObserver?.disconnect();
    // Reset footer height when component is destroyed
    this.layoutService.setFooterHeight(0);
  }

  /**
   * Updates the layout service with the current physical height of the footer.
   */
  private updateFooterHeight() {
    const height = this.elementRef.nativeElement.offsetHeight;
    this.layoutService.setFooterHeight(height);
  }

  /**
   * Updates the 'isHighlighted' state of cards based on the matching URL.
   * @param url The current navigation URL.
   */
  private updateHighlightedCard(url: string) {
    const matchingCard = this.cards().find(
      (card) => card.route && url.includes(card.route)
    );
    if (matchingCard) {
      this.cards().forEach((c) => (c.isHighlighted = false));
      matchingCard.isHighlighted = true;
      this.dashboardService.setSelectedDashboard(matchingCard.id);
    }
  }

  /**
   * Selection handler when a card background or content is interacted with.
   * @param card The DashboardCard object that was clicked.
   */
  onCardClick(card: DashboardCard) {
    this.cardClick.emit(card);

    if (card.route) {
      this.router.navigate([card.route]);
    }
  }

  /** Closes the notifications/updates drawer. */
  onDrawerClose() {
    this.drawerVisible = false;
  }

  /**
   * Handles navigate to manifest create logic.
   */
  navigateToManifestCreate() {
    this.router.navigate(['/settings/manifest-templates'], {
      queryParams: { mode: 'new' },
    });
  }

  /**
   * Handles navigate to declaration template logic.
   */
  navigateToDeclarationTemplate() {
    this.router.navigate(['/settings/declaration-templates']);
  }
}
